import React, { FC, MouseEvent, useEffect, useMemo, useState, ChangeEvent, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Error as ErrorIcon, Search, HighlightOff, Clear } from '@mui/icons-material';
import { CircularProgress, Link, Menu, Icon, InputAdornment, Dialog } from '@mui/material';
import { format, addDays } from 'date-fns';
import { debounce } from 'lodash';
import ReactGA from 'react-ga4';
import { toast } from 'react-toastify';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import Button from 'components/Button/Button';
import { ChooseColumnsMenu } from 'components/CommissionHistoryTableV2/ChooseColumnsMenu/ChooseColumnsMenu';
import { CommissionHistoryTableV2 } from 'components/CommissionHistoryTableV2/CommissionHistoryTable/CommissionHistoryTableV2';
import { FilterStatusMenu } from 'components/CommissionHistoryTableV2/FilterStatusMenu/FilterStatusMenu';
import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import TextField from 'components/TextField/TextField';
import { exportJSONToCSV } from 'helpers';
import { hasWildfirePermissionsSelector } from 'helpers/auth0';
import { DATE_FORMAT } from 'helpers/constants';
import useTimeOnPageGA from 'hooks/useTimeOnPageGA';
import { useGetApplicationDetailsQuery } from 'reduxState/store/application/api';
import { useCreateCommissionDownloadMutation, useLazyGetCommissionsQuery } from 'reduxState/store/commission/api';
import { commissionColumnsSelector } from 'reduxState/store/commission/selectors';
import {
  CombinedColumn,
  CommissionColumn,
  CommissionColumnVisible,
  userCommissionColumn,
  FilterStatus,
  IDisplayCommission,
  CsvCommission,
  CsvCommissionColumn,
} from 'reduxState/store/commission/types';
import exportIcon from 'static/images/export-icon-black.png';
import infoIcon from 'static/images/info-icon.png';
import menuBookIcon from 'static/images/menu-book-black.png';
import { useAppSelector, useAppDispatch } from '../../reduxState/hooks';
import { setCommissionColumns } from '../../reduxState/store/commission/slice';

import './CommissionHistory.scss';

interface OwnProps {
  applicationId: string;
  adminAppId: string;
  appName: string;
  advancedSearch?: boolean;
}

const EDIT_COLUMNS_MENU_ID = 'edit-columns-menu';
const FILTER_STATUS_MENU_ID = 'filter-status-menu';

const CommissionHistory: FC<React.PropsWithChildren<OwnProps>> = ({
  applicationId,
  advancedSearch,
  adminAppId,
  appName,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { user } = useAuth0();
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadedCommissions, setLoadedCommissions] = useState<IDisplayCommission[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [callbackVisible, setCallbackVisible] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState(false);
  const [editColumnsAnchorEl, setEditColumnsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterStatusAnchorEl, setFilterStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [statusFilters, setStatusFilters] = useState<FilterStatus>({
    PENDING: true,
    READY: true,
    PAID: true,
    CONFIRMED: true,
  });
  const [getCommissions, { error: commissionError }] = useLazyGetCommissionsQuery();
  const [createCommissionDownloadJob, { error: commissionDownloadError }] = useCreateCommissionDownloadMutation();

  const hasWfPermissions = hasWildfirePermissionsSelector(user);
  useTimeOnPageGA(user, applicationId, 'Commission History');

  const columns = useAppSelector(commissionColumnsSelector);

  const currentDate = new Date();
  const oneWeekAgo = new Date(currentDate.setDate(currentDate.getDate() - 7));

  const [startDate, setStartDate] = useState<Date | null>(oneWeekAgo);
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const handleGetCommissions = async (cursorParam?: string, pageSize?: number): Promise<{ commissions: IDisplayCommission[]; nextCursor: string | null }> => {
    const appId = applicationId;
    if (!startDate || !endDate) {
      toast.error('Please select start/end date.');
      return { commissions: [], nextCursor: null };
    }

    try {
      const { displayCommissions, nextCursor } = await getCommissions({
        adminId: adminAppId,
        filters: {
          startDate: format(new Date(startDate), DATE_FORMAT),
          endDate: format(addDays(new Date(endDate), 1), DATE_FORMAT),
          searchQueries: searchTerm,
          cursor: cursorParam,
          sortBy: null,
          sortOrder: 'asc',
          pageSize: pageSize || 100,
          // advancedSearch is being set at component rendering, it is only available for /commission-search-advanced-search
          advancedSearch,
          ids: appId,
        },
      }).unwrap();
      return { commissions: displayCommissions || [], nextCursor };
    } catch (error) {
      console.error('Failed to retrieve commission data', error);
      toast.error('Failed to retrieve commission data.');
      return { commissions: [], nextCursor: null };
    }
  };

  const generateCommissions = async () => {
    setIsLoading(true);
    setHasNextPage(true);
    setLoadedCommissions([]);
    setCursor(null);
    const { commissions: newCommissions, nextCursor } = await handleGetCommissions();
    if (newCommissions) {
      setLoadedCommissions(newCommissions);
      setCursor(nextCursor);
      setHasNextPage(nextCursor !== null);
      setIsLoading(false);
    }
  };

  const handleLoadMoreCommissions = async (): Promise<void> => {
    try {
      const { commissions: newCommissions, nextCursor } = await handleGetCommissions(cursor ?? undefined);
      if (newCommissions.length) {
        setLoadedCommissions(loadedCommissions.concat(newCommissions));
      }
      setCursor(nextCursor);
      setHasNextPage(nextCursor !== null);
    } catch (error) {
      console.error('Failed to load more commissions.', error);
    }
  };

  const handleDateChange = (dateRange: DateRange | null) => {
    if (!dateRange) return;
    const [start, end] = dateRange;

    setStartDate(start);
    setEndDate(end);

    if (!hasWfPermissions && user) {
      ReactGA.event('click_date', {
        appId: applicationId,
        userId: user!.sub,
        startDate,
        endDate,
        pageName: 'Commission History',
      });
    }
  };

  const onDatePickerClear = () => {
    setStartDate(null);
    setEndDate(null);
  };

  useEffect(() => {
    if (searchTerm) {
      generateCommissions();
      !hasWfPermissions &&
        ReactGA.event('search', {
          appId: applicationId,
          userId: user!.sub,
          pageName: 'Commission History',
          searchTerm: searchTerm,
        });
      return;
    }
    if (!startDate || !endDate) return;
    generateCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, searchTerm, applicationId]);

  const { data: application, error: errorGettingApplication } = useGetApplicationDetailsQuery({
    appId: applicationId,
  });

  if (errorGettingApplication) {
    toast.error('Failed to retrieve application details.');
    console.error(errorGettingApplication);
  }

  useEffect(() => {
    if (!application || application.Policy === null) {
      return;
    }

    const has3WaySplit = application.Policy.length === 3;
    const hasDeviceSplit = application.Policy.find(s => s.Part === 'DEVICE');
    const hasUserCommissionColumn = columns.find(
      (c: CombinedColumn) => isCommissionColumn(c) && c.accessor === userCommissionColumn.accessor,
    );

    if (has3WaySplit && hasDeviceSplit) {
      if (!hasUserCommissionColumn) {
        dispatch(
          setCommissionColumns({
            columns: [...columns, userCommissionColumn],
          }),
        );
      }
    } else {
      dispatch(
        setCommissionColumns({
          columns: columns.filter(
            (c: CombinedColumn) => isCommissionColumn(c) && c.accessor !== userCommissionColumn.accessor,
          ),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, application]);

  const exportToCsv = async (): Promise<void> => {
    if (!startDate || !endDate) return;
    setIsDownloading(true);
    try {
      const { commissions: csvCommissions, nextCursor } = await handleGetCommissions(undefined, 2000);
      if (nextCursor !== null || csvCommissions.length >= 2000) {
        try {
          const queryString =
            `EventDate:>"${format(startDate, DATE_FORMAT)}" ` +
            `EventDate:<"${format(endDate, DATE_FORMAT)}" ` +
            encodeURIComponent(searchTerm);
          await createCommissionDownloadJob({
            appId: applicationId,
            userToken: user!.sub!,
            queryString,
          }).unwrap();
          setModalOpen(true);
        } catch (error) {
          setModalOpen(true);
        }

        return setIsDownloading(false);
      }

      /**
       *  Commissions formatted with the data for Sale Amount, Partner Commission, and User Commission.
       *  Each of these fields initially contains an object with a property for both amount and currency data.
       *  To make these values compatible with CSV format, this restructuring splits each field
       *  into two separate columns: one for the amount and another for the currency.
       * */
      const reformattedCommissions: CsvCommission[] = csvCommissions.map(column => ({
        ...column,
        SaleAmount: column.SaleAmount?.amount,
        SaleAmountCurrency: column.SaleAmount?.currency,
        PartnerCommission: column.PartnerCommission?.amount,
        PartnerCommissionCurrency: column.PartnerCommission?.currency,
        BaseCommission: column.BaseCommission?.amount,
        BaseCommissionCurrency: column.BaseCommission?.currency,
        BoostedCommission: column.BoostedCommission?.amount,
        BoostedCommissionCurrency: column.BoostedCommission?.currency,
        UserCommission: column.UserCommission?.amount,
        UserCommissionCurrency: column.UserCommission?.currency,
      }));

      /**
       * Columns formatted for new Currency column corresponding to Sale Amount, Partner Commission, Boosted Commission, and User Commission
       * */
      const reformattedColumns: CsvCommissionColumn[] = [];
      visibleColumns.forEach(column => {
        reformattedColumns.push(column);
        if (
          column.accessor === 'SaleAmount' ||
          column.accessor === 'PartnerCommission' ||
          column.accessor === 'BaseCommission' ||
          column.accessor === 'BoostedCommission' ||
          column.accessor === 'UserCommission'
        ) {
          reformattedColumns.push({
            name: `${column.name} Currency`,
            accessor: `${column.accessor}Currency`,
          });
        }
      });
      const csv = exportJSONToCSV(reformattedCommissions, reformattedColumns);

      if (csv) {
        const blob = new Blob([csv], { type: 'text/csv' });

        const a = document.createElement('a');
        a.style.display = 'none';
        document.body.appendChild(a);
        a.href = URL.createObjectURL(blob);

        a.setAttribute(
          'download',
          `${appName.split(' ').join('_')}_Commission_History_${format(new Date(startDate), DATE_FORMAT)}_to_${format(
            new Date(endDate),
            DATE_FORMAT,
          )}.csv`,
        );

        // trigger download by simulating click
        a.click();

        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);

        closeEditColumnsMenu();
        !hasWfPermissions &&
          ReactGA.event('click_export', {
            action: 'click',
            appId: applicationId,
            userId: user!.sub,
            pageName: 'Commission History',
            totalRecords: loadedCommissions.length,
          });
      } else {
        setDownloadError(true);
        setTimeout((): void => setDownloadError(false), 3000);
      }
    } catch (error) {
      console.error('Failed to export commission data.', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const openEditColumnsMenu = (event: MouseEvent<HTMLButtonElement>): void => {
    setEditColumnsAnchorEl(event.currentTarget);
    !hasWfPermissions &&
      ReactGA.event('click_edit', {
        appId: applicationId,
        userId: user!.sub,
        pageName: 'Commission History',
      });
  };

  const closeEditColumnsMenu = (): void => {
    setEditColumnsAnchorEl(null);
  };

  const updateColumns = (nextColumns?: CombinedColumn[]): void => {
    if (!nextColumns) return;

    dispatch(
      setCommissionColumns({
        columns: nextColumns as CommissionColumn[],
      }),
    );
  };

  const closeFilterStatusMenu = (): void => {
    setFilterStatusAnchorEl(null);
    if (hasDatesSelected) {
      generateCommissions();
    }
  };

  const updateStatusFilters = (status: keyof FilterStatus): void => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 500),
    [setSearchTerm],
  );

  const onSearchCommissionHistoryHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      debouncedSearch(event.target.value);
    },
    [debouncedSearch],
  );

  const onClickClear = (): void => {
    setInputValue('');
    setSearchTerm('');
  };

  const toggleCallback = (): void => {
    setCallbackVisible(x => !x);
  };

  const visibleColumns = useMemo((): CommissionColumnVisible[] => {
    const hasShoppingTripCodes = loadedCommissions.some(commission => !!commission.ShoppingTripCode);
    const hasDeviceXIDs = loadedCommissions.some(commission => !!commission.DeviceXID);

    const newColumns = columns.map(column => {
      if (
        (column.name === 'Shopping Trip Code' && !hasShoppingTripCodes) ||
        (column.name === 'Device XID' && !hasDeviceXIDs)
      ) {
        return { ...column, visible: false };
      }
      return column;
    });
    return newColumns.filter(
      (column: CombinedColumn): column is CommissionColumnVisible => 'visible' in column && column.visible,
    );
  }, [columns, loadedCommissions]);

  const hasDatesSelected = useMemo((): boolean => {
    return !!startDate && !!endDate;
  }, [startDate, endDate]);

  const addHeaderMargin = useMemo((): boolean => {
    return loadedCommissions.length > 20;
  }, [loadedCommissions]);

  function isCommissionColumn(column: CombinedColumn): column is CommissionColumn {
    return (column as CommissionColumn).accessor !== undefined;
  }

  return (
    <div className="commission-history-v2">
      <div className="commission-history-v2-title--block">
        <h1 className="text-muted-dark-purple">Commission History</h1>
      </div>
      <div className="commission-history-v2-header">
        <DateRangePicker
          label="Event Date"
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          onDateChange={handleDateChange}
          onDateClear={onDatePickerClear}
        />
        <div className="actions">
          <TextField
            className="table-filter-field commission-history-v2-text-field"
            variant="outlined"
            placeholder="Search"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <div className="search-icon">
                    {inputValue ? (
                      <HighlightOff onClick={onClickClear} className="search-bar-icon" />
                    ) : (
                      <Search className="search-bar-icon" />
                    )}
                  </div>
                </InputAdornment>
              ),
            }}
            onChange={(event): void => {
              event.persist();
              setInputValue(event.target.value);
              onSearchCommissionHistoryHandler(event);
            }}
            value={inputValue}
          />

          <div className="btn-dropdown">
            <Button
              className="button-table-action"
              disabled={false}
              variant="outlined"
              aria-controls={EDIT_COLUMNS_MENU_ID}
              aria-haspopup="true"
              endIcon={
                <Icon>
                  <img src={menuBookIcon} height={18} width={18} alt="Columns icon" />
                </Icon>
              }
              onClick={openEditColumnsMenu}
              fullWidth={true}
            >
              Edit Columns
            </Button>

            <Menu
              id={EDIT_COLUMNS_MENU_ID}
              anchorEl={editColumnsAnchorEl}
              keepMounted
              open={!!editColumnsAnchorEl}
              onClose={closeEditColumnsMenu}
            >
              <ChooseColumnsMenu
                columns={columns}
                updateColumns={updateColumns}
                callbackVisible={callbackVisible}
                toggleCallback={toggleCallback}
                isInAppGroupOverview={false}
              />
            </Menu>
            <Menu
              id={FILTER_STATUS_MENU_ID}
              anchorEl={filterStatusAnchorEl}
              keepMounted
              open={!!filterStatusAnchorEl}
              onClose={closeFilterStatusMenu}
            >
              <FilterStatusMenu statusFilters={statusFilters} updateStatusFilters={updateStatusFilters} />
            </Menu>
            <Button
              className="progress-wrapper button-table-action"
              disabled={loadedCommissions.length === 0 || downloadError || isDownloading || !hasDatesSelected}
              onClick={exportToCsv}
              endIcon={
                downloadError ? (
                  <ErrorIcon />
                ) : (
                  <Icon>
                    <img src={exportIcon} height={18} width={18} alt="Export icon" />
                  </Icon>
                )
              }
            >
              {isDownloading && <CircularProgress className="progress" size={18} />}
              Export
            </Button>
            <Link href="https://kb.wildfire-corp.com/article/ygwr-commission-history" target="_blank">
              <Icon>
                <img className="help-icon" src={infoIcon} height={24} width={24} alt="More information icon" />
              </Icon>
            </Link>
          </div>
        </div>
      </div>
      <CommissionHistoryTableV2
        columns={visibleColumns}
        commissions={loadedCommissions}
        applicationId={applicationId}
        isLoading={isLoading}
        hasDatesSelected={hasDatesSelected}
        hasNextPage={hasNextPage}
        loadMoreItems={handleLoadMoreCommissions}
        hasCommissionError={Boolean(commissionError)}
        addHeaderMargin={addHeaderMargin}
        callbackVisible={callbackVisible}
        updateColumns={updateColumns}
        isInAppGroupOverview={false}
      />
      {modalOpen && (
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          classes={{
            paper: 'modal-padding',
          }}
        >
          <div>
            <div className="close-icon" onClick={() => setModalOpen(false)}>
              <Clear />
            </div>
            <p>The data you requested is too large to download directly.</p>
            {commissionDownloadError ? (
              <p>
                There was an issue starting a download process for your data, please try to download your data again to
                create a new download process.
              </p>
            ) : (
              <p>
                Please visit the{' '}
                <RouterLink
                  className="font-bold underline text-href-blue"
                  to={`/${adminAppId}/app/${applicationId}/commission-download`}
                >
                  Commission History Download
                </RouterLink>{' '}
                page, where it will be available to download soon.
              </p>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default CommissionHistory;
